import type { TickerSnapshot } from "@ekidenfi/ts-sdk";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";

import { useStores } from "@stores";

import type { Market } from "@src/api";
import { getEkidenSdkClient } from "@src/api/sdk";

interface MarketInfoResponse extends Partial<TickerSnapshot> {
  addr: string;
  symbol: string;
  base_decimals?: number;
  quote_decimals?: number;
  quote_addr?: string;
  root?: string;
  base_symbol?: string;
  created_at?: string;
  updated_at?: string;
  epoch?: string;
  funding_epoch?: string;
  funding_index?: string;
  funding_rate_percentage?: string;
  initial_margin_ratio?: string;
  maintenance_margin_ratio?: string;
  max_leverage?: string;
  min_order_size?: string;
  open_interest_value?: string;
  oracle_price?: string;
  price_feed_id?: string;
  maker_fee_rate?: number;
  taker_fee_rate?: number;
}

const convertMarketResponseToMarket = (response: MarketInfoResponse): Market => {
  const sanitize = (val: string | number | null | undefined) =>
    val === "NaN" || val === null || val === undefined ? "0" : String(val);

  return {
    addr: response.addr || "",
    base_decimals: response.base_decimals || 8,
    created_at: response.created_at || new Date().toISOString(),
    epoch: sanitize(response.epoch),
    funding_epoch: sanitize(response.funding_epoch),
    funding_index: sanitize(response.funding_index),
    funding_rate_percentage: sanitize(response.funding_rate_percentage || String(Number(response.funding_rate) * 100)),
    id: 0,
    initial_margin_ratio: sanitize(response.initial_margin_ratio || "0.1"),
    maintenance_margin_ratio: sanitize(response.maintenance_margin_ratio || "0.05"),
    mark_price: sanitize(response.mark_price),
    last_price: sanitize(response.last_price),
    max_leverage: sanitize(response.max_leverage || "10"),
    min_order_size: sanitize(response.min_order_size || "0.1"),
    open_interest: sanitize(response.open_interest),
    open_interest_value: sanitize(response.open_interest_value),
    volume_24h: sanitize(response.volume_24h),
    turnover_24h: sanitize(response.turnover_24h),
    oracle_price: sanitize(response.oracle_price || response.index_price),
    quote_addr: response.quote_addr || "",
    quote_decimals: response.quote_decimals || 6,
    root: response.root || "",
    symbol: response.symbol || "",
    base_symbol: response.base_symbol || response.symbol || "",
    updated_at: response.updated_at || new Date().toISOString(),
    price_feed_id: response.price_feed_id || "",
    maint_margin: response.maintenance_margin_ratio || "0.05",
    maker_fee_rate: response.maker_fee_rate || 0,
    taker_fee_rate: response.taker_fee_rate || 0,
  };
};

let globalIsLoading = false;
let globalHasLoaded = false;

export const MarketStoreLoader = observer(() => {
  const { marketStore, configStore, isAppReady } = useStores();

  useEffect(() => {
    const loadMarkets = async () => {
      if (!configStore.isConfigAvailable || globalHasLoaded || globalIsLoading || !isAppReady) return;
      globalIsLoading = true;

      try {
        const marketResponses = await getEkidenSdkClient().market.getMarkets();
        const markets = marketResponses.map(convertMarketResponseToMarket);

        if (markets.length) {
          await marketStore.loadMarkets(markets);

          if (!marketStore.activeMarket) {
            const id = marketStore.requiredMarketId;

            if (id) {
              const requiredMarket = markets.find((m: Market) => m.symbol.includes(id));
              if (requiredMarket) {
                marketStore.setActiveMarket(requiredMarket);
              } else {
                marketStore.setActiveMarket(markets[0]);
              }
            } else {
              marketStore.setActiveMarket(markets[0]);
            }
          }

          await marketStore.loadTickers();
          await marketStore.loadInstrumentsInfo();

          // Load funding rate data from REST API for active market
          if (marketStore.activeMarket) {
            await marketStore.loadMarketData(marketStore.activeMarket);
          }

          globalHasLoaded = true;
        }
      } catch {
        // Error loading markets
      } finally {
        globalIsLoading = false;
      }
    };

    loadMarkets();
  }, [marketStore, configStore.isConfigAvailable, isAppReady]);

  return null;
});
